import { useState, useEffect, useCallback } from 'react';
import { useToast } from '@chakra-ui/react';
import { trainerService, GetTrainerClientsParams, TrainerClient } from './trainerService';

export interface UseTrainerClientsResult {
    clients: TrainerClient[];
    isLoading: boolean;
    error: string | null;
    totalCount: number;
    currentPage: number;
    totalPages: number;
    fetchClients: (params?: GetTrainerClientsParams) => Promise<void>;
    setCurrentPage: (page: number) => void;
    refreshClients: () => Promise<void>;
}

export interface UseTrainerClientsOptions {
    page?: number;
    limit?: number;
    active_only?: boolean;
    autoFetch?: boolean;
}

export const useTrainerClients = (options: UseTrainerClientsOptions = {}): UseTrainerClientsResult => {
    const {
        page = 1,
        limit = 10,
        active_only,
        autoFetch = true
    } = options;

    const [clients, setClients] = useState<TrainerClient[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [totalCount, setTotalCount] = useState(0);
    const [currentPage, setCurrentPage] = useState(page);

    const toast = useToast();

    const fetchClients = useCallback(async (params: GetTrainerClientsParams = {}) => {
        setIsLoading(true);
        setError(null);

        try {
            const response = await trainerService.getTrainerClients({
                page: currentPage,
                limit,
                active_only,
                ...params,
            });

            setClients(response.clients || []);
            setTotalCount(response.total_count || 0);
        } catch (err: any) {
            const errorMessage = err.message || 'Failed to fetch clients';
            setError(errorMessage);

            toast({
                title: 'Error',
                description: errorMessage,
                status: 'error',
                duration: 5000,
                isClosable: true,
            });
        } finally {
            setIsLoading(false);
        }
    }, [currentPage, limit, active_only, toast]);

    const refreshClients = useCallback(async () => {
        await fetchClients();
    }, [fetchClients]);

    useEffect(() => {
        if (autoFetch) {
            fetchClients();
        }
    }, [fetchClients, autoFetch]);

    const totalPages = Math.ceil(totalCount / limit);

    return {
        clients,
        isLoading,
        error,
        totalCount,
        currentPage,
        totalPages,
        fetchClients,
        setCurrentPage,
        refreshClients,
    };
};
